/**
 * @breathly/config — shared configuration shape.
 *
 * Per FOLDER_STRUCTURE.md: this package holds environment variable contracts,
 * feature flags, and constants that multiple surfaces must agree on.
 *
 * No secrets — secrets are never committed. Only shape and default values live here.
 */

/** Environment names (per DEPLOYMENT.md). */
export type AppEnvironment = 'dev' | 'staging' | 'prod';

/** Base URL configuration for the API. */
export interface ApiConfig {
  /** Base URL of the FastAPI backend (e.g., http://localhost:8000). */
  readonly baseUrl: string;
  /** API version prefix (e.g., "v1"). */
  readonly version: string;
}

/** Build the full API base path from config. */
export function apiBasePath(config: ApiConfig): string {
  const base = config.baseUrl.replace(/\/+$/, '');
  const version = config.version ? `/api/${config.version}` : '';
  return `${base}${version}`;
}

/** Environment variable keys — canonical names used across apps and the backend. */
export const ENV_KEYS = {
  API_BASE_URL: 'VITE_API_BASE_URL',
  APP_ENV: 'APP_ENV',
  SUPABASE_URL: 'SUPABASE_URL',
  SUPABASE_ANON_KEY: 'SUPABASE_ANON_KEY',
} as const;

/**
 * Feature flags — typed, versioned, and toggleable without redeploy where possible.
 * Flags live here as defaults; server-side flags override when present.
 */
export interface FeatureFlags {
  /** Enable the companion chat surface (future). */
  readonly companionChat: boolean;
  /** Enable experiment proposal flow. */
  readonly experiments: boolean;
  /** Enable preparation plans for social events. */
  readonly preparationPlans: boolean;
}

/** Default feature flags — all off until features are implemented. */
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  companionChat: false,
  experiments: false,
  preparationPlans: false,
};

/**
 * Shared constants that multiple surfaces must agree on.
 * These supplement @breathly/shared/constants but are configuration-specific
 * (vs. product constants which live in @breathly/shared).
 */
export const CONFIG = {
  /** Default API version. */
  API_VERSION: 'v1',
  /** Default API base path prefix (without host). */
  API_BASE_PATH: '/api/v1',
  /** Health check route. */
  HEALTH_PATH: '/health',
} as const;
